import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthContext } from '../contexts/AuthContext'
import * as cadService from '../services/cadService'
import GenerationLoader from '../components/GenerationLoader'
import {
  FiBox,
  FiLoader,
  FiAlertTriangle,
  FiDownload,
  FiArrowLeft,
  FiRotateCcw,
  FiRotateCw,
  FiTrash2,
  FiSend,
  FiPackage,
  FiDollarSign,
  FiLayers,
  FiInfo,
  FiClock,
} from 'react-icons/fi'
import './CADDesignerPage.css'

const MAX_COMMAND_LENGTH = 2000

const isWebGLAvailable = () => {
  try {
    const canvas = document.createElement('canvas')
    const gl =
      canvas.getContext('webgl') || canvas.getContext('experimental-webgl')
    if (!gl) return false
    // Stricter check: disabled environments report "Disabled" as renderer
    const ext = gl.getExtension('WEBGL_debug_renderer_info')
    if (ext) {
      const renderer =
        (gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) || '').toLowerCase()
      if (renderer.includes('disabled')) return false
    }
    // Try a minimal WebGL operation - some sandboxed contexts fail here
    gl.getParameter(gl.VERSION)
    return true
  } catch {
    return false
  }
}

const CADDesignerPage = () => {
  const { token } = useAuthContext()
  const navigate = useNavigate()
  const modelViewerRef = useRef(null)

  // State management
  const [webglAvailable, setWebglAvailable] = useState(true)
  const [modelViewerLoaded, setModelViewerLoaded] = useState(false)
  const [modelViewerError, setModelViewerError] = useState(false)
  const [command, setCommand] = useState('')
  const [sessionId, setSessionId] = useState(null)
  const [response, setResponse] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [modelBlobUrl, setModelBlobUrl] = useState(null)
  const [generationTime, setGenerationTime] = useState(null)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const startTimeRef = useRef(null)
  const timerIntervalRef = useRef(null)

  // Example commands for quick access
  const exampleCommands = [
    'Crea una casa 10x8 m con 2 piani e tetto rosso',
    'Aggiungi una porta sul lato sud',
    'Aggiungi 3 finestre sul lato nord',
    'Crea una scatola 5x5x3 m',
    'Sposta la casa di 2m a est',
  ]

  // Fetch authenticated file and create blob URL
  const getAuthenticatedFileBlob = async (url, authToken) => {
    try {
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.status}`)
      }

      const blob = await response.blob()
      return URL.createObjectURL(blob)
    } catch (error) {
      console.error('Error fetching authenticated file:', error)
      throw error
    }
  }

  // Detect WebGL support on mount
  useEffect(() => {
    setWebglAvailable(isWebGLAvailable())
  }, [])

  // Lazy-load model-viewer script only when we have a model and WebGL
  useEffect(() => {
    if (!modelBlobUrl || !webglAvailable) {
      setModelViewerLoaded(false)
      setModelViewerError(false)
      return
    }
    if (document.querySelector('script[src*="model-viewer"]')) {
      setModelViewerLoaded(true)
      return
    }
    setModelViewerError(false)
    const script = document.createElement('script')
    script.type = 'module'
    script.src =
      'https://ajax.googleapis.com/ajax/libs/model-viewer/3.3.0/model-viewer.min.js'
    script.onload = () => setModelViewerLoaded(true)
    script.onerror = () => setModelViewerError(true)
    document.head.appendChild(script)
  }, [modelBlobUrl, webglAvailable])

  // Cleanup blob URL when component unmounts or model changes
  useEffect(() => {
    return () => {
      if (modelBlobUrl) {
        URL.revokeObjectURL(modelBlobUrl)
      }
    }
  }, [modelBlobUrl])

  // Load 3D model when response changes
  useEffect(() => {
    let isMounted = true
    let previousBlobUrl = modelBlobUrl

    const loadModel = async () => {
      if (!response?.files?.glb || !sessionId) {
        // If no model available, cleanup and clear
        if (previousBlobUrl && isMounted) {
          URL.revokeObjectURL(previousBlobUrl)
          setModelBlobUrl(null)
        }
        return
      }

      try {
        const url = cadService.getCADFileURL(sessionId, 'glb')
        const blobUrl = await getAuthenticatedFileBlob(url, token)

        // Cleanup previous blob URL after new one is loaded
        if (previousBlobUrl && previousBlobUrl !== blobUrl) {
          URL.revokeObjectURL(previousBlobUrl)
        }

        if (isMounted) {
          setModelBlobUrl(blobUrl)
        } else {
          // Cleanup if component unmounted during fetch
          URL.revokeObjectURL(blobUrl)
        }
      } catch (error) {
        console.error('Failed to load 3D model:', error)
        if (isMounted) {
          setError('Impossibile caricare il modello 3D')
        }
      }
    }

    loadModel()

    return () => {
      isMounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response?.files?.glb, sessionId, token])

  const handleBack = () => {
    navigate(-1)
  }

  const handleSubmit = async e => {
    e?.preventDefault()
    if (!command.trim() || isLoading) return

    setIsLoading(true)
    setError('')
    setGenerationTime(null)
    setElapsedSeconds(0)
    startTimeRef.current = Date.now()

    // Start timer - update every second
    timerIntervalRef.current = setInterval(() => {
      if (startTimeRef.current) {
        const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000)
        setElapsedSeconds(elapsed)
      }
    }, 1000)

    try {
      const result = await cadService.processCADCommand(
        token,
        command.trim(),
        sessionId
      )

      // Calculate generation time
      if (startTimeRef.current) {
        const timeTaken = ((Date.now() - startTimeRef.current) / 1000).toFixed(1)
        setGenerationTime(parseFloat(timeTaken))
      }

      setSessionId(result.session_id)
      setResponse(result)
      setCommand('')
    } catch (err) {
      console.error('CAD Error:', err)
      const errorMessage =
        err.response?.data?.detail ||
        err.message ||
        'Impossibile elaborare il comando CAD'
      setError(errorMessage)
    } finally {
      setIsLoading(false)
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current)
        timerIntervalRef.current = null
      }
      startTimeRef.current = null
    }
  }

  const handleUndo = async () => {
    if (!sessionId || !response?.can_undo || isLoading) return

    setIsLoading(true)
    setError('')

    try {
      const result = await cadService.undoCAD(token, sessionId)
      setResponse(result)
    } catch (err) {
      setError(err.response?.data?.detail || 'Impossibile annullare')
    } finally {
      setIsLoading(false)
    }
  }

  const handleRedo = async () => {
    if (!sessionId || !response?.can_redo || isLoading) return

    setIsLoading(true)
    setError('')

    try {
      const result = await cadService.redoCAD(token, sessionId)
      setResponse(result)
    } catch (err) {
      setError(err.response?.data?.detail || 'Impossibile ripetere')
    } finally {
      setIsLoading(false)
    }
  }

  const handleClearSession = async () => {
    if (!sessionId) return

    const confirmClear = window.confirm(
      'Sei sicuro di voler cancellare la sessione corrente? Questo eliminerà tutto il lavoro.'
    )
    if (!confirmClear) return

    try {
      await cadService.deleteCADSession(token, sessionId)
      setSessionId(null)
      setResponse(null)
      setError('')
    } catch (err) {
      setError(err.response?.data?.detail || 'Impossibile cancellare la sessione')
    }
  }

  const handleExampleClick = exampleCommand => {
    setCommand(exampleCommand)
  }

  const handleDownload = async format => {
    if (!sessionId) return

    try {
      const url = cadService.getCADFileURL(sessionId, format)
      const blobUrl = await getAuthenticatedFileBlob(url, token)

      // Create download link
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = `cad-model-${sessionId}.${format}`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      // Cleanup blob URL after download
      setTimeout(() => URL.revokeObjectURL(blobUrl), 100)
    } catch (error) {
      console.error('Download failed:', error)
      setError(`Impossibile scaricare il file ${format.toUpperCase()}`)
    }
  }

  const formatFileSize = bytes => {
    if (!bytes) return 'N/A'
    return (bytes / 1024).toFixed(1) + ' KB'
  }

  const formatCost = cost => {
    if (cost === undefined || cost === null) return 'N/A'
    return '$' + cost.toFixed(2)
  }

  const formatVolume = liters => {
    if (liters === undefined || liters === null) return 'N/A'
    return liters.toFixed(2) + ' L'
  }

  return (
    <div className="cad-designer-page">
      {/* Header */}
      <div className="cad-header">
        <div className="header-controls">
          <button className="back-button" onClick={handleBack}>
            <FiArrowLeft />
          </button>
          <h1>
            <FiBox /> F.R.A.N.C.A. CAD Designer
          </h1>
          <div className="header-actions">
            <button
              className="icon-button"
              onClick={handleUndo}
              disabled={!response?.can_undo || isLoading}
              title="Annulla"
            >
              <FiRotateCcw />
            </button>
            <button
              className="icon-button"
              onClick={handleRedo}
              disabled={!response?.can_redo || isLoading}
              title="Ripeti"
            >
              <FiRotateCw />
            </button>
            <button
              className="icon-button danger"
              onClick={handleClearSession}
              disabled={!sessionId || isLoading}
              title="Cancella Sessione"
            >
              <FiTrash2 />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="cad-content">
        {/* Left Panel - Command Input and Info */}
        <div className="cad-left-panel">
          {/* Command Input */}
          <div className="command-section">
            <h2>
              <FiSend /> Inserisci Comando
            </h2>
            <form onSubmit={handleSubmit}>
              <textarea
                className="command-input"
                value={command}
                onChange={e => setCommand(e.target.value)}
                placeholder="Descrivi il modello 3D che vuoi creare (es: 'Crea un data center a 4 piani con tetto a pannelli solari, dimensioni 50x30m')"
                rows={4}
                maxLength={MAX_COMMAND_LENGTH}
              />
              <div className="input-footer">
                <span
                  className={
                    command.length > MAX_COMMAND_LENGTH * 0.9 ? 'text-warning' : ''
                  }
                >
                  {command.length}/{MAX_COMMAND_LENGTH}
                </span>
                <button
                  type="submit"
                  className="submit-button"
                  disabled={!command.trim() || isLoading}
                >
                  {isLoading ? (
                    <>
                      <FiLoader className="spinner" /> Elaborazione...
                    </>
                  ) : (
                    <>
                      <FiSend /> Genera
                    </>
                  )}
                </button>
              </div>
            </form>

            {/* Error Message */}
            {error && (
              <div className="error-message">
                <FiAlertTriangle /> {error}
              </div>
            )}

            {/* Success Message */}
            {response && !error && (
              <div className="success-message">
                <FiInfo /> {response.message}
                {generationTime && (
                  <span className="generation-time-badge">
                    <FiClock /> Generato in {generationTime}s
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Example Commands */}
          <div className="examples-section">
            <h3>Esempi di Comandi</h3>
            <div className="example-commands">
              {exampleCommands.map((example, index) => (
                <button
                  key={index}
                  className="example-button"
                  onClick={() => handleExampleClick(example)}
                  disabled={isLoading}
                >
                  {example}
                </button>
              ))}
            </div>
          </div>

          {/* Bill of Materials */}
          {response?.scene?.bom && (
            <div className="bom-section">
              <h3>
                <FiPackage /> Distinta Materiali
              </h3>
              <div className="bom-summary">
                <div className="bom-item">
                  <FiLayers />
                  <div>
                    <span className="bom-label">Volume Totale</span>
                    <span className="bom-value">
                      {formatVolume(response.scene.bom.liters)}
                    </span>
                  </div>
                </div>
                <div className="bom-item">
                  <FiDollarSign />
                  <div>
                    <span className="bom-label">Costo Stimato</span>
                    <span className="bom-value">
                      {formatCost(response.scene.bom.rough_cost_usd)}
                    </span>
                  </div>
                </div>
              </div>

              {response.scene.bom.items?.length > 0 && (
                <div className="bom-items-list">
                  <h4>Oggetti</h4>
                  {response.scene.bom.items.map((item, index) => (
                    <div key={index} className="bom-list-item">
                      <span className="item-id">{item.id}</span>
                      <span className="item-type">{item.object}</span>
                      <span className="item-volume">
                        {(item.approx_volume_mm3 / 1000000).toFixed(2)} L
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Download Section */}
          {response?.files && (
            <div className="download-section">
              <h3>
                <FiDownload /> Scarica File
              </h3>
              <div className="download-buttons">
                {Object.entries(response.files).map(([format, file]) => (
                  <button
                    key={format}
                    className="download-button"
                    onClick={() => handleDownload(format)}
                  >
                    <FiDownload />
                    <div className="download-info">
                      <span className="download-format">{format.toUpperCase()}</span>
                      <span className="download-size">
                        {formatFileSize(file.size_bytes)}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Panel - 3D Viewer */}
        <div className="cad-right-panel">
          <div className="viewer-container">
            {isLoading ? (
              <GenerationLoader
                type="cad"
                elapsedSeconds={elapsedSeconds}
                message="Stiamo costruendo il tuo modello parametrico 3D..."
              />
            ) : modelBlobUrl ? (
              !webglAvailable || modelViewerError ? (
                <div className="viewer-placeholder viewer-webgl-fallback">
                  <FiBox size={64} />
                  <p>Anteprima 3D non disponibile in questo ambiente</p>
                  <p className="placeholder-hint">
                    WebGL non supportato o disabilitato. Scarica il modello per
                    visualizzarlo con un&apos;app 3D.
                  </p>
                  <div className="fallback-download-buttons">
                    <button
                      type="button"
                      className="submit-button"
                      onClick={() => handleDownload('glb')}
                    >
                      <FiDownload /> Scarica GLB
                    </button>
                    <button
                      type="button"
                      className="submit-button secondary"
                      onClick={() => handleDownload('stl')}
                    >
                      <FiDownload /> Scarica STL
                    </button>
                  </div>
                </div>
              ) : !modelViewerLoaded ? (
                <div className="viewer-placeholder">
                  <FiLoader className="spinner" style={{ marginBottom: '1rem' }} />
                  <p>Caricamento viewer 3D...</p>
                </div>
              ) : (
                <div
                  key={modelBlobUrl}
                  style={{ width: '100%', height: '100%', position: 'relative' }}
                >
                  <model-viewer
                    ref={el => {
                      modelViewerRef.current = el
                      if (el) {
                        el.addEventListener('error', () => setModelViewerError(true))
                      }
                    }}
                    src={modelBlobUrl}
                    alt="3D CAD Model"
                    auto-rotate
                    camera-controls
                    shadow-intensity="1"
                    style={{ width: '100%', height: '100%' }}
                  />
                  <div className="viewer-overlay">
                    <div className="viewer-hint">
                      🖱️ Trascina per ruotare • Scroll per zoom • Click destro per
                      spostare
                    </div>
                  </div>
                </div>
              )
            ) : (
              <div className="viewer-placeholder">
                <FiBox size={64} />
                <p>Il tuo modello 3D apparirà qui</p>
                <p className="placeholder-hint">
                  Inserisci un comando per iniziare a creare
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default CADDesignerPage
